import { Alert, Box, Button, Chip, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import {
  CONNECTION_MODES,
  SERVICE_LABELS,
  SERVICE_USES,
  providerDef,
  providersOf,
} from '@app/shared';
import type {
  CarrierConnectionDto,
  CarrierConnectionInput,
  CarrierConnectionsDto,
  CarrierEventDto,
  ConnectionDto,
  ConnectionInput,
  ConnectionMode,
  ConnectionTestDto,
  MvneConnectorDto,
  ReadinessItemDto,
} from '@app/shared';
import { ConfirmDialog } from '../../components/dialogs';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { ApiError, api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { Link } from 'react-router-dom';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { CarrierConnectorsSection, TryRequestDialog } from './CarrierConnectors';

const KEY = ['connections'];

const STATE_COLOR = {
  Connected: 'success',
  Simulated: 'warning',
  'Not connected': 'error',
} as const;

/** One service: its provider, mode, values, Save, Test connection and Remove. */
function ServiceCard({ c }: { c: ConnectionDto }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const choices = providersOf(c.service);
  const [provider, setProvider] = useState(
    c.provider ?? (choices.length === 1 ? choices[0]!.key : ''),
  );
  const [mode, setMode] = useState<ConnectionMode>(c.mode ?? 'Sandbox');
  const [status, setStatus] = useState<'Active' | 'Inactive'>(c.status ?? 'Active');
  const [values, setValues] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [removing, setRemoving] = useState(false);
  const def = providerDef(provider);

  // Plain values start from what is stored; secrets start empty ("leave empty to keep").
  useEffect(() => {
    const start: Record<string, string> = {};
    for (const field of providerDef(provider)?.fields ?? [])
      start[field.key] = field.secret || provider !== c.provider ? '' : (c.values[field.key] ?? '');
    setValues(start);
    setProblems({});
  }, [provider, c]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
  const save = useMutation({
    mutationFn: (body: ConnectionInput) =>
      api.put<ConnectionDto>(`/connections/${c.service}`, body),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: () => api.post<ConnectionTestDto>(`/connections/${c.service}/test`),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/connections/${c.service}`),
    onSuccess: refresh,
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!provider) return setProblems({ provider: 'Select Provider' });
    try {
      await save.mutateAsync({ provider, mode, status, values });
      notify.success(`${SERVICE_LABELS[c.service]} saved. Press Test connection to check it.`);
    } catch (err) {
      const found =
        err instanceof ApiError
          ? Object.fromEntries(err.details.map((d) => [d.path.replace(/^values\./, ''), d.message]))
          : {};
      setProblems(found);
      if (Object.keys(found).length === 0) notify.error(err);
    }
  };

  const runTest = async () => {
    try {
      const r = await test.mutateAsync();
      if (r.ok) notify.success(r.message);
      else notify.error(new Error(r.message));
    } catch (err) {
      notify.error(err);
    }
  };

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={SERVICE_LABELS[c.service]}
      sx={{ p: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="h3">{SERVICE_LABELS[c.service]}</Typography>
        <Chip size="small" color={STATE_COLOR[c.state]} label={c.state} />
        {c.mode ? <Chip size="small" variant="outlined" label={c.mode} /> : null}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {SERVICE_USES[c.service]}
      </Typography>
      <Box component="form" onSubmit={(e) => void submit(e)} sx={{ display: 'grid', gap: 1.5 }}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
          <TextField
            select
            required
            label="Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            error={!!problems.provider}
            helperText={problems.provider}
          >
            {choices.map((p) => (
              <MenuItem key={p.key} value={p.key}>
                {p.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            required
            label="Mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as ConnectionMode)}
          >
            {CONNECTION_MODES.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
          >
            {['Active', 'Inactive'].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {def ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {mode === 'Sandbox' ? def.sandbox : 'Live: real messages, charges or labels.'} Where
              to find the values: {def.where}
            </Typography>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
              {def.fields.map((field) => {
                const stored = provider === c.provider ? c.values[field.key] : '';
                return (
                  <TextField
                    key={field.key}
                    label={field.label}
                    required={field.required && !(field.secret && stored)}
                    type={field.secret ? 'password' : 'text'}
                    autoComplete="off"
                    value={values[field.key] ?? ''}
                    onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                    error={!!problems[field.key]}
                    helperText={
                      problems[field.key] ??
                      (field.secret && stored
                        ? `Stored (${stored}). Leave empty to keep it.`
                        : field.help)
                    }
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                    select={!!field.options}
                  >
                    {field.options?.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              })}
            </Box>
          </>
        ) : null}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            Save
          </Button>
          {c.provider ? (
            <>
              <Button variant="outlined" onClick={() => void runTest()} disabled={test.isPending}>
                {test.isPending ? 'Testing…' : 'Test connection'}
              </Button>
              <Button color="error" onClick={() => setRemoving(true)}>
                Remove
              </Button>
            </>
          ) : null}
          {c.lastTest ? (
            <Typography
              variant="body2"
              color={c.lastTest.ok ? 'success.main' : 'error.main'}
              aria-label={`Last test of ${SERVICE_LABELS[c.service]}`}
            >
              Last test {formatDateTime(c.lastTest.at)}: {c.lastTest.message}
            </Typography>
          ) : null}
        </Box>
        {c.updatedAt ? (
          <Typography variant="caption" color="text.secondary">
            Saved {formatDateTime(c.updatedAt)} by {c.updatedBy}
          </Typography>
        ) : null}
      </Box>
      <ConfirmDialog
        open={removing}
        title={`Remove the ${SERVICE_LABELS[c.service]} connection`}
        message="Its values are deleted. Until it is connected again, the service is simulated or refused."
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onClose={() => setRemoving(false)}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => {
              setRemoving(false);
              notify.success('Connection removed.');
            },
            onError: (err) => notify.error(err),
          })
        }
      />
    </Paper>
  );
}

/** One carrier of Plan Config and its MVNE connection (connector, mode, credentials). */
function CarrierCard({
  c,
  connectors,
}: {
  c: CarrierConnectionDto;
  connectors: MvneConnectorDto[];
}) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [connector, setConnector] = useState(
    c.connector ?? (connectors.length === 1 ? connectors[0]!.key : ''),
  );
  const [mode, setMode] = useState<ConnectionMode>(c.mode ?? 'Sandbox');
  const [status, setStatus] = useState<'Active' | 'Inactive'>(c.status ?? 'Active');
  const [values, setValues] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [showCallbacks, setShowCallbacks] = useState(false);
  const [trying, setTrying] = useState(false);
  const featureOn = useFeatureReleased();
  const def = connectors.find((x) => x.key === connector);
  useEffect(() => {
    const start: Record<string, string> = {};
    for (const field of connectors.find((x) => x.key === connector)?.fields ?? [])
      start[field.key] =
        field.secret || connector !== c.connector ? '' : (c.values[field.key] ?? '');
    setValues(start);
    setProblems({});
  }, [connector, c, connectors]);
  const base = `/connections/carriers/${c.carrierId}`;
  const refresh = () => queryClient.invalidateQueries({ queryKey: CARRIERS_KEY });
  const save = useMutation({
    mutationFn: (body: CarrierConnectionInput) => api.put<CarrierConnectionDto>(base, body),
    onSuccess: refresh,
  });
  const test = useMutation({
    mutationFn: () => api.post<ConnectionTestDto>(`${base}/test`),
    onSuccess: refresh,
  });
  const callbacks = useQuery({
    queryKey: [...CARRIERS_KEY, c.carrierId, 'callbacks'],
    queryFn: () => api.get<{ data: CarrierEventDto[] }>(`${base}/callbacks`),
    select: (r) => r.data,
    enabled: showCallbacks,
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!connector) return setProblems({ connector: 'Select MVNE' });
    try {
      await save.mutateAsync({ connector, mode, status, values });
      notify.success(`Carrier ${c.carrierCode} saved. Press Test connection to check it.`);
    } catch (err) {
      const found =
        err instanceof ApiError
          ? Object.fromEntries(err.details.map((d) => [d.path.replace(/^values\./, ''), d.message]))
          : {};
      setProblems(found);
      if (Object.keys(found).length === 0) notify.error(err);
    }
  };
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`Carrier ${c.carrierCode}`}
      sx={{ p: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="h3">
          {c.carrierCode} ({c.carrierName})
        </Typography>
        <Chip size="small" color={STATE_COLOR[c.state]} label={c.state} />
        {c.mode ? <Chip size="small" variant="outlined" label={c.mode} /> : null}
      </Box>
      {connectors.length === 0 ? (
        <Alert severity="info">No MVNE connector yet. Make one under Carrier connectors.</Alert>
      ) : (
        <Box component="form" onSubmit={(e) => void submit(e)} sx={{ display: 'grid', gap: 1.5 }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr 1fr' } }}>
            <TextField
              select
              required
              label="MVNE"
              value={connector}
              onChange={(e) => setConnector(e.target.value)}
              error={!!problems.connector}
              helperText={problems.connector}
            >
              {connectors.map((x) => (
                <MenuItem key={x.key} value={x.key}>
                  {x.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              required
              label="Mode"
              value={mode}
              onChange={(e) => setMode(e.target.value as ConnectionMode)}
            >
              {CONNECTION_MODES.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'Active' | 'Inactive')}
            >
              {['Active', 'Inactive'].map((x) => (
                <MenuItem key={x} value={x}>
                  {x}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          {def ? (
            <>
              <Typography variant="body2" color="text.secondary">
                {mode === 'Sandbox' ? def.sandbox : 'Live: real lines.'} Where to find the values:{' '}
                {def.where}
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
                {def.fields.map((field) => {
                  const stored = connector === c.connector ? c.values[field.key] : '';
                  return (
                    <TextField
                      key={field.key}
                      label={field.label}
                      required={field.required && !(field.secret && stored)}
                      type={field.secret ? 'password' : 'text'}
                      autoComplete="off"
                      value={values[field.key] ?? ''}
                      onChange={(e) => setValues((v) => ({ ...v, [field.key]: e.target.value }))}
                      error={!!problems[field.key]}
                      helperText={
                        problems[field.key] ??
                        (field.secret && stored
                          ? `Stored (${stored}). Leave empty to keep it.`
                          : field.help)
                      }
                    />
                  );
                })}
              </Box>
            </>
          ) : null}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button type="submit" variant="contained" disabled={save.isPending}>
              Save
            </Button>
            {c.connector ? (
              <>
                <Button
                  variant="outlined"
                  disabled={test.isPending}
                  onClick={() =>
                    test.mutate(undefined, {
                      onSuccess: (r) =>
                        r.ok ? notify.success(r.message) : notify.error(new Error(r.message)),
                      onError: (err) => notify.error(err),
                    })
                  }
                >
                  {test.isPending ? 'Testing…' : 'Test connection'}
                </Button>
                <Button onClick={() => setShowCallbacks((v) => !v)}>
                  {showCallbacks ? 'Hide callbacks' : 'Callbacks received'}
                </Button>
                {c.connector.startsWith('custom:') && featureOn('carrier-connector-builder') ? (
                  <Button onClick={() => setTrying(true)}>Try a request</Button>
                ) : null}
              </>
            ) : null}
            {c.lastTest ? (
              <Typography variant="body2" color={c.lastTest.ok ? 'success.main' : 'error.main'}>
                Last test {formatDateTime(c.lastTest.at)}: {c.lastTest.message}
              </Typography>
            ) : null}
          </Box>
          {c.callbackUrl ? (
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              Callback address to give the MVNE: <code>{c.callbackUrl}</code>
            </Typography>
          ) : null}
          {showCallbacks ? (
            <Box aria-label={`Callbacks of ${c.carrierCode}`}>
              {(callbacks.data ?? []).length === 0 ? (
                <Typography variant="body2">No callback received yet.</Typography>
              ) : (
                (callbacks.data ?? []).map((e) => (
                  <Typography key={e.id} variant="body2">
                    {formatDateTime(e.receivedAt)} · {e.status} · {e.note}
                  </Typography>
                ))
              )}
            </Box>
          ) : null}
        </Box>
      )}
      {trying && c.connector ? (
        <TryRequestDialog
          carrierId={c.carrierId}
          carrierCode={c.carrierCode}
          connectorKey={c.connector}
          onClose={() => setTrying(false)}
        />
      ) : null}
    </Paper>
  );
}

const CARRIERS_KEY = ['connections', 'carriers'];

const MARK = { ok: '✔', todo: '✖', info: 'ℹ' } as const;
const MARK_COLOR = { ok: 'success.main', todo: 'error.main', info: 'text.secondary' } as const;

/** The go-live checklist: what is ready for real customers and what is missing. */
function ReadinessSection() {
  const featureOn = useFeatureReleased();
  const list = useQuery({
    queryKey: ['connections', 'readiness'],
    queryFn: () => api.get<{ data: ReadinessItemDto[] }>('/connections/readiness'),
    select: (r) => r.data,
  });
  if (!list.data) return null;
  const left = list.data.filter((i) => i.status === 'todo').length;
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label="Go-live checklist"
      sx={{ p: 2, mb: 2 }}
    >
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        Go-live checklist
      </Typography>
      <Typography variant="body2" color={left ? 'error.main' : 'success.main'} sx={{ mb: 1 }}>
        {left
          ? `${left} item(s) to do before taking real customers.`
          : 'Everything here is ready for real customers.'}{' '}
        Backups and the domain are checked on the server (docs/deployment.md).
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}>
        {list.data.map((i) => (
          <Box component="li" key={i.key} sx={{ display: 'flex', gap: 1 }}>
            <Typography component="span" color={MARK_COLOR[i.status]} aria-label={i.status}>
              {MARK[i.status]}
            </Typography>
            <Typography component="span" variant="body2">
              <b>{i.label}</b>: {i.detail}
            </Typography>
          </Box>
        ))}
      </Box>
      {featureOn('customer-import') ? (
        <Button component={Link} to="/settings/customer-import" size="small" sx={{ mt: 1 }}>
          Import customers from the previous system
        </Button>
      ) : null}
    </Paper>
  );
}

/** Settings > Connections > Carriers: the MVNE account behind each carrier of Plan Config. */
function CarriersSection() {
  const list = useQuery({
    queryKey: CARRIERS_KEY,
    queryFn: () => api.get<CarrierConnectionsDto>('/connections/carriers'),
  });
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h2" sx={{ mb: 1 }}>
        Carriers (MVNE)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each carrier of Plan Config &gt; Carrier Setup is connected to the MVNE that carries its
        lines, with your own MVNE account. Activations, SIM and number changes, suspensions, plans,
        top-ups, ports and usage then go to the MVNE for real. An MVNE 212Ez has no connector for is
        set up under Carrier connectors, below.
      </Typography>
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !list.data ? (
        <LoadingState />
      ) : list.data.carriers.length === 0 ? (
        <Alert severity="info">Add the carriers first in Plan Config &gt; Carrier Setup.</Alert>
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {list.data.carriers.map((c) => (
            <CarrierCard key={c.carrierId} c={c} connectors={list.data.connectors} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Settings > Connections (PC239, administrators): the company's own accounts with SendGrid, Twilio,
 * USPS, ShipStation, Stripe and CereTax, the MVNE behind each carrier, and the carrier connectors
 * made on the page.
 */
export function ConnectionsPage({ title = 'Connections' }: { title?: string }) {
  const { user } = useAuth();
  const featureOn = useFeatureReleased();
  const list = useQuery({
    queryKey: KEY,
    queryFn: () => api.get<{ data: ConnectionDto[] }>('/connections'),
    select: (r) => r.data,
    enabled: user?.level === 'admin',
  });
  return (
    <Box>
      <PageHeader title={title} code="PC239" />
      {user?.level !== 'admin' ? (
        <Alert severity="info">Only an administrator manages the connections.</Alert>
      ) : (
        <>
          <ReadinessSection />
          <Alert severity="info" sx={{ mb: 2 }}>
            Each service uses your own account with the provider. Secrets are stored encrypted and
            are never shown again. A service that is not connected is <b>Simulated</b> until the
            release switch &quot;no simulation&quot; is ticked; from then on it is refused with a
            message. Each carrier is connected to its MVNE under Carriers, below.
          </Alert>
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : !list.data ? (
            <LoadingState />
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {list.data.map((c) => (
                <ServiceCard key={c.service} c={c} />
              ))}
            </Box>
          )}
          <CarriersSection />
          {featureOn('carrier-connector-builder') ? <CarrierConnectorsSection /> : null}
        </>
      )}
    </Box>
  );
}
